import Sidebar from "../sidebar";
import Navbar from "../navbar";
import Title from "../title";
import Footer from "../footer";
import PageTools from "../dashboard/page-tools";
import { useCallback, useContext, useEffect, useState } from "react";
import { Outlet } from "react-router";
import SpecialOffer from "../dashboard/special-offer";
import FileOpApi from "../../api/file-op";
import AuthContext from "../auth-context";
import CustomerApi from "../../api/customer";
import PurchaseTicketApi from "../../api/purchase-ticket";
import moment from "moment-jalaali";
import Container from "../tailwind/container";
import Modal from "../tailwind/modal";
import ModalDialog from "../tailwind/modal-dialog";
import ModalContent from "../tailwind/modal-content";
import FileApi from "../../api/file";
import AppTour from "../appTour";

const ACTIVE_FILE_STORAGE_KEY = "activeFileId";

export default function MainContent() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [files, setFiles] = useState([]);
  const [hasEditRequest, setHasEditRequest] = useState(false);
  const [isCompany, setIsCompany] = useState(false);

  const context = useContext(AuthContext);

  const {
    activeFileId,
    setActiveFileId,
    tickets: openTickets,
    customerInfo: customer,
    setCustomerInfo: setCustomer,
  } = context;

  const handleActiveFileChange = useCallback(
    (fileId) => {
      if (fileId == null || fileId === "") {
        localStorage.removeItem(ACTIVE_FILE_STORAGE_KEY);
        setActiveFileId("");
        return;
      }

      const normalizedFileId = String(fileId);

      localStorage.setItem(ACTIVE_FILE_STORAGE_KEY, normalizedFileId);

      setActiveFileId(normalizedFileId);
    },
    [setActiveFileId],
  );

  const getAllTickets = useCallback(() => {
    new PurchaseTicketApi(context)
      .getAll()
      .then((data) => {
        context.setTickets(data);
      })
      .catch(() => {});
  }, [context]);

  const getCustomerData = useCallback(() => {
    const customerApi = new CustomerApi(context);

    customerApi
      .get()
      .then((data) => {
        setCustomer(data);
        setIsCompany(data.company != null);
      })
      .catch(() => {});

    const fileApi = new FileApi(context);

    fileApi
      .getAll()
      .then((data) => {
        setFiles(data);

        if (!data || data.length === 0) {
          localStorage.removeItem(ACTIVE_FILE_STORAGE_KEY);
          setActiveFileId("");
          return;
        }

        const savedFileId = localStorage.getItem(ACTIVE_FILE_STORAGE_KEY);

        const savedFileExists =
          savedFileId != null &&
          data.some((file) => String(file.id) === String(savedFileId));

        if (data.length === 1) {
          const onlyFileId = String(data[0].id);

          localStorage.setItem(ACTIVE_FILE_STORAGE_KEY, onlyFileId);

          setActiveFileId(onlyFileId);
          return;
        }

        if (savedFileId && savedFileExists) {
          setActiveFileId(String(savedFileId));
          return;
        }

        const currentFileExists =
          activeFileId != null &&
          activeFileId !== "" &&
          data.some((file) => String(file.id) === String(activeFileId));

        if (currentFileExists) {
          const normalizedActiveFileId = String(activeFileId);

          localStorage.setItem(ACTIVE_FILE_STORAGE_KEY, normalizedActiveFileId);

          setActiveFileId(normalizedActiveFileId);
          return;
        }

        localStorage.removeItem(ACTIVE_FILE_STORAGE_KEY);
        setActiveFileId("");
      })
      .catch(() => {});

    getAllTickets();
  }, [context, activeFileId, setActiveFileId, setCustomer, getAllTickets]);

  useEffect(() => {
    getCustomerData();
  }, []);

  const specialTicket = openTickets?.find((ticket) => ticket.isSpecial);

  const handleEditTitle = (newTitle) => {
    if (activeFileId == null || activeFileId === "" || !newTitle) {
      setHasEditRequest(false);
      return;
    }

    new FileOpApi(context)
      .ChangeComment(activeFileId, newTitle)
      .then(() => {
        getCustomerData();
      })
      .catch(() => {})
      .finally(() => {
        setHasEditRequest(false);
      });
  };

  const activeFile = files.find(
    (file) => String(file.id) === String(activeFileId ?? ""),
  );

  return (
    <>
      <AppTour filesCount={files.length} />

      <div className="main-container">
        <Sidebar
          customer={customer}
          isCollapsed={isSidebarCollapsed}
          isVisible={isSidebarVisible}
          onCollapse={setIsSidebarCollapsed}
          onShow={setIsSidebarVisible}
        />

        <div role="main" className="main-content">
          <Navbar
            customer={customer}
            isCollapsed={isSidebarCollapsed}
            onCollapse={setIsSidebarCollapsed}
            isVisible={isSidebarVisible}
            onShow={setIsSidebarVisible}
            openTickes={openTickets}
            onTicketChange={getAllTickets}
          />

          <Container className="page-content">
            {specialTicket && (
              <SpecialOffer
                expireDate={moment(specialTicket.expireDate)}
                titles={specialTicket.titles}
                price={specialTicket.price}
                id={specialTicket.id}
              />
            )}

            <div className="page-header border-0 pb-3 row">
              <Title
                files={files}
                currentId={activeFileId}
                onChange={handleActiveFileChange}
                onEdit={() => setHasEditRequest(true)}
                title={activeFile?.title}
                comment={activeFile?.comment}
              />

              <Modal show={hasEditRequest}>
                <ModalDialog>
                  <ModalContent
                    title={activeFile?.title}
                    comment={activeFile?.comment}
                    onEdit={handleEditTitle}
                    onCancel={() => setHasEditRequest(false)}
                  />
                </ModalDialog>
              </Modal>

              <PageTools isCompany={isCompany} />
            </div>

            <Outlet />
          </Container>

          <Footer />
        </div>
      </div>
    </>
  );
}
